import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { formatMoney, formatQty, UNITS, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, FormError, PageHeader, Panel, Select, Spinner, useToast } from '../../ui';

interface ExpiryBatch {
  id: string;
  productId: string;
  name: string;
  sku: string;
  unit: string;
  branchId: string;
  branchName: string;
  batchNo: string | null;
  expiryDate: string;
  daysLeft: number;
  expired: boolean;
  qtyMilli: number;
  valueCents: number | null;
}
interface ExpiryReport {
  today: string;
  batches: ExpiryBatch[];
  expiredValueCents: number;
  soonValueCents: number;
}

const unitShort = (u: string) => UNITS[u as keyof typeof UNITS]?.short ?? u;

export function ExpiryPage() {
  const { me, can, hasModule } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [days, setDays] = useState('14');
  const [branchId, setBranchId] = useState('');
  const [writing, setWriting] = useState<ExpiryBatch | null>(null);
  const q = useQuery({
    queryKey: ['expiry', days, branchId],
    queryFn: () => api.get<ExpiryReport>(`/api/expiry?days=${days}${branchId ? `&branchId=${branchId}` : ''}`),
  });
  const seeValue = can('products.view_cost');
  const expired = q.data?.batches.filter((b) => b.expired) ?? [];
  const soon = q.data?.batches.filter((b) => !b.expired) ?? [];

  return (
    <>
      <PageHeader
        title="Expiry dates"
        description="Stock past or close to its expiry date. The till already refuses expired batches and sells the earliest-expiring stock first."
      />
      {q.data && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2">
          <Stat label="Expired, still on the books" value={`${expired.length} batch${expired.length === 1 ? '' : 'es'}`} sub={seeValue ? `${formatMoney(q.data.expiredValueCents, cur)} at cost` : undefined} tone={expired.length ? 'danger' : undefined} />
          <Stat label={`Expiring in the next ${days} days`} value={`${soon.length} batch${soon.length === 1 ? '' : 'es'}`} sub={seeValue ? `${formatMoney(q.data.soonValueCents, cur)} at cost` : undefined} tone={soon.length ? 'warn' : undefined} />
        </div>
      )}
      <Panel>
        <div className="flex flex-wrap gap-3 border-b border-line px-5 py-3">
          <Select aria-label="Look ahead" className="max-w-56" value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="0">Expired only</option>
            <option value="3">Next 3 days</option>
            <option value="7">Next 7 days</option>
            <option value="14">Next 14 days</option>
            <option value="30">Next 30 days</option>
            <option value="90">Next 90 days</option>
          </Select>
          {me.branches.length > 1 && (
            <Select aria-label="Branch" className="max-w-56" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          )}
        </div>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} onRetry={() => q.refetch()} /></div>}
        {q.data?.batches.length === 0 && <EmptyState title="Nothing expiring in this period">Batches appear here as their expiry date gets close.</EmptyState>}
        {!!q.data?.batches.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Product</th>
                  <th className="px-3 py-2.5 font-medium">Batch</th>
                  <th className="px-3 py-2.5 font-medium">Expiry</th>
                  <th className="px-3 py-2.5 text-right font-medium">Quantity</th>
                  {seeValue && <th className="px-3 py-2.5 text-right font-medium">Cost value</th>}
                  {can('expiry.manage') && <th className="px-5 py-2.5"><span className="sr-only">Action</span></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.batches.map((b) => (
                  <tr key={b.id}>
                    <td className="px-5 py-3">
                      <span className="font-medium">{b.name}</span>
                      <span className="block text-xs text-muted">{b.sku} — {b.branchName}</span>
                    </td>
                    <td className="px-3 py-3 text-sm">{b.batchNo ?? '—'}</td>
                    <td className="px-3 py-3">
                      <span className="text-sm num">{b.expiryDate}</span>
                      <span className="ml-2">
                        {b.expired ? (
                          <Badge tone="danger">Expired {-b.daysLeft === 0 ? 'today' : `${-b.daysLeft} day${b.daysLeft === -1 ? '' : 's'} ago`}</Badge>
                        ) : (
                          <Badge tone={b.daysLeft <= 3 ? 'warn' : 'neutral'}>{b.daysLeft === 0 ? 'Last day today' : `${b.daysLeft} day${b.daysLeft === 1 ? '' : 's'} left`}</Badge>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right num">{formatQty(b.qtyMilli)} {unitShort(b.unit)}</td>
                    {seeValue && <td className={clsx('px-3 py-3 text-right num', b.expired && 'text-danger')}>{b.valueCents != null ? formatMoney(b.valueCents, cur) : '—'}</td>}
                    {can('expiry.manage') && (
                      <td className="px-5 py-3 text-right">
                        {b.expired && <Button size="sm" onClick={() => setWriting(b)}>Write off</Button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {writing && <WriteOffDialog batch={writing} asWastage={hasModule('wastage')} onClose={() => setWriting(null)} />}
    </>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'danger' | 'warn' }) {
  return (
    <div className="rounded-[var(--radius-panel)] border border-line bg-surface px-5 py-4">
      <p className="text-sm text-muted">{label}</p>
      <p className={clsx('mt-1 text-2xl font-semibold num', tone === 'danger' && 'text-danger', tone === 'warn' && 'text-warn')}>{value}</p>
      {sub && <p className="text-sm text-muted num">{sub}</p>}
    </div>
  );
}

function WriteOffDialog({ batch, asWastage, onClose }: { batch: ExpiryBatch; asWastage: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post<{ number: string }>(`/api/batches/${batch.id}/write-off`),
    onSuccess: (r) => {
      toast(`${r.number}: ${batch.name} written off`);
      for (const k of ['expiry', 'products', 'wastage', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Write off ${batch.name}?`}
      description={`Batch ${batch.batchNo ?? 'without a number'}, expired ${batch.expiryDate}, at ${batch.branchName}.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" busy={m.isPending} onClick={() => m.mutate()}>Write off {formatQty(batch.qtyMilli)} {unitShort(batch.unit)}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <p>
        Takes the {formatQty(batch.qtyMilli)} {unitShort(batch.unit)} left in this batch out of stock at average cost
        {asWastage ? ', recorded as wastage (past expiry date).' : ', as a stock adjustment marked expired.'} Throw the stock away or return it to the supplier
        before you confirm.
      </p>
    </Dialog>
  );
}
